// ─────────────────────────────────────────────────────────────────────────────
// dagScaleModel — the data model behind the DAG "Scale" view (the LOD grouping
// treemap). It exposes ONE uniform shape the canvas renderer draws from, backed by
// two very different sources:
//
//   • REAL mode    — the project's actual provenance graph, layered by longest path
//                    (sources at layer 0, matching the explorer's `buildMap` depth
//                    pass). Tiles map to real node ids; connectors + the lineage
//                    cone are derived from the REAL edges (NOT a synthetic rule).
//   • SYNTHETIC mode — a rule-based layered DAG up to 1,000,000 nodes whose topology
//                    is never materialised: layer counts come from a closed-form lens
//                    profile and wiring is the analytic rule node(L+1,j) ← node(L,
//                    ⌊j·cL/cR⌋). This is the "flat in N to 1M" scale demo.
//
// Pure + framework-free (mirrors dagLenses.ts / dagLayouts.ts) so it is unit-testable
// and the canvas component stays a thin imperative shell.
// ─────────────────────────────────────────────────────────────────────────────

import type { Graph } from "@flexcompute/thread-graph";
import { ancestors, descendants, type GraphIndex } from "@flexcompute/thread-graph";

export type ScaleMode = "real" | "synthetic";

/** Band-height envelope (world units) — shared by both modes so the lens reads the same. */
const HB_MAX = 1400;
const HB_MIN = 46; // = BOX.h in the renderer

export interface ScaleTileInfo {
  /** True when the tile is a single leaf (one real node / one synthetic run). */
  single: boolean;
  title: string;
  sub: string | null;
}

export interface ScaleNodeDetail {
  kind: string;
  title: string;
  up: number;
  down: number;
  inputs: number;
  /** Pretty-printed metadata JSON (real value_json, or synthetic fields). */
  json: string;
  /** The real node id for a single-node tile — used to re-root the other views; null for groups/synthetic. */
  realId: string | null;
}

/** A contiguous tile = a chunk of layer L's members in [i0, i1). */
export interface ScaleModel {
  mode: ScaleMode;
  /** Total node count (real) or N (synthetic). */
  n: number;
  /** Number of layers. */
  nL: number;
  /** Members per layer. */
  count: number[];
  /** Per-layer band height (log-scaled envelope). */
  bandH: number[];
  /** Aggregate edge weight between layer L and L+1. */
  flow: number[];
  maxFlow: number;
  maxC: number;
  /** Set by the renderer's fitCanvas — the overview baseline (1 box per layer). */
  fitZ: number;
  layerKind(L: number): string;
  tileInfo(L: number, i0: number, i1: number): ScaleTileInfo;
  detail(L: number, i0: number, i1: number): ScaleNodeDetail;
  /** Does tile [i0,i1) in layer L feed tile [j0,j1) in layer L+1? */
  connects(L: number, i0: number, i1: number, j0: number, j1: number): boolean;
  /** The real node id of member i in layer L (single-tile re-root); null for synthetic. */
  realIdAt(L: number, i: number): string | null;
  /**
   * Build the lineage predicate for a selected tile [i0,i1) in layer L. Returns a
   * function `(layer, a, b) => boolean` — true when tile [a,b) in `layer` is on the
   * selected node's lineage (so the renderer lights it and greys the rest).
   */
  coneOf(L: number, i0: number, i1: number): (layer: number, a: number, b: number) => boolean;
}

// ── shared helpers ───────────────────────────────────────────────────────────
const lg = (c: number, maxC: number) => Math.log(1 + c) / Math.log(1 + Math.max(1, maxC));
const bandHeights = (count: number[], maxC: number) =>
  count.map((c) => HB_MIN + (HB_MAX - HB_MIN) * lg(c, maxC));
export const fmtCount = (c: number) =>
  c >= 1e6 ? (c / 1e6).toFixed(c >= 1e7 ? 0 : 1) + "M" : c >= 1000 ? Math.round(c / 1000) + "k" : "" + c;

// Short kind tag used in tile/group labels.
const KIND_SHORT: Record<string, string> = {
  geometry: "geo", run: "run", function: "op", operation: "op", saved_plot: "plot", plot: "plot",
};
const shortKind = (k: string) => KIND_SHORT[k] ?? (k.length > 4 ? k.slice(0, 4) : k);

// ═════════════════════════════════════════════════════════════════════════════
// SYNTHETIC mode — analytic, never materialised. Verbatim port of the sandbox.
// ═════════════════════════════════════════════════════════════════════════════
function synId(L: number, i: number, kindOf: (L: number) => string) {
  return `${shortKind(kindOf(L))}-${String(i).padStart(5, "0")}`;
}
function synMetric(L: number, i: number, kindOf: (L: number) => string) {
  let s = (L * 2654435761 + i * 40503) >>> 0;
  const r = ((s ^ (s >>> 13)) % 1000) / 1000,
    k = kindOf(L);
  if (k === "run") return `CL ${(0.1 + r * 0.9).toFixed(3)}`;
  if (k === "geometry") return `mesh ${10 + Math.floor(r * 90)}k`;
  if (k === "saved_plot") return `fig ${1 + Math.floor(r * 9)}`;
  return `Δ ${(r * 2 - 1).toFixed(3)}`;
}

export function buildSyntheticModel(N: number): ScaleModel {
  const nL = 20;
  const count = new Array<number>(nL).fill(1);
  count[0] = Math.min(4, N);
  count[nL - 1] = 1;
  const prof: number[] = [];
  let psum = 0;
  for (let L = 1; L < nL - 1; L++) {
    const t = L / (nL - 1);
    prof[L] = Math.pow(Math.sin(t * Math.PI), 2.6);
    psum += prof[L]!;
  }
  const Nint = Math.max(nL - 2, N - count[0]! - count[nL - 1]!);
  for (let L = 1; L < nL - 1; L++) count[L] = Math.max(1, Math.round((Nint * prof[L]!) / psum));
  const maxC = Math.max(...count);
  const bandH = bandHeights(count, maxC);
  const flow: number[] = [];
  let maxFlow = 0;
  for (let L = 0; L < nL - 1; L++) {
    flow[L] = Math.max(count[L]!, count[L + 1]!);
    if (flow[L]! > maxFlow) maxFlow = flow[L]!;
  }
  const layerKind = (L: number) =>
    L === 0 ? "geometry" : L === nL - 1 ? "saved_plot" : L < 4 || L >= nL - 4 ? "function" : "run";

  return {
    mode: "synthetic",
    n: N,
    nL,
    count,
    bandH,
    flow,
    maxFlow,
    maxC,
    fitZ: 0,
    layerKind,
    tileInfo(L, i0, i1) {
      const size = i1 - i0;
      if (size === 1) return { single: true, title: synId(L, i0, layerKind), sub: synMetric(L, i0, layerKind) };
      return { single: false, title: `${fmtCount(size)} ${shortKind(layerKind(L))}s`, sub: `${synId(L, i0, layerKind)} … ${synId(L, i1 - 1, layerKind)}` };
    },
    detail(L, i0, i1) {
      const size = i1 - i0,
        kind = layerKind(L),
        short = shortKind(kind);
      const inDeg = L > 0 ? Math.max(1, Math.round(count[L - 1]! / count[L]!)) : 0;
      const outDeg = L < nL - 1 ? Math.max(1, Math.round(count[L + 1]! / count[L]!)) : 0;
      const obj =
        size === 1
          ? { id: synId(L, i0, layerKind), kind, layer: `${L + 1} of ${nL}`, metric: synMetric(L, i0, layerKind), inputs: inDeg, feeds: outDeg }
          : { group: `${fmtCount(size)} ${short}s`, range: `${synId(L, i0, layerKind)} … ${synId(L, i1 - 1, layerKind)}`, kind, layer: `${L + 1} of ${nL}`, feeds_per_node: outDeg };
      return { kind, title: size === 1 ? synId(L, i0, layerKind) : `${fmtCount(size)} ${short}s`, up: inDeg, down: outDeg, inputs: inDeg, json: JSON.stringify(obj, null, 2), realId: null };
    },
    connects(L, i0, i1, j0, j1) {
      // analytic wiring node(L+1,j) ← node(L, ⌊j·cL/cR⌋): tile [i0,i1) feeds the proportional dst range.
      const cL = count[L]!,
        cR = count[L + 1]!;
      const s0 = Math.floor((i0 * cR) / cL),
        s1 = Math.max(s0 + 1, Math.ceil((i1 * cR) / cL));
      return j1 > s0 && j0 < s1;
    },
    realIdAt: () => null,
    coneOf(L, i0, i1) {
      const lo = new Array<number>(nL),
        hi = new Array<number>(nL);
      lo[L] = i0;
      hi[L] = i1;
      for (let l = L; l > 0; l--) {
        const cP = count[l - 1]!,
          cT = count[l]!;
        lo[l - 1] = Math.floor((lo[l]! * cP) / cT);
        hi[l - 1] = Math.max(lo[l - 1]! + 1, Math.min(cP, Math.ceil((hi[l]! * cP) / cT)));
      }
      for (let l = L; l < nL - 1; l++) {
        const cT = count[l]!,
          cN = count[l + 1]!;
        lo[l + 1] = Math.floor((lo[l]! * cN) / cT);
        hi[l + 1] = Math.max(lo[l + 1]! + 1, Math.min(cN, Math.ceil((hi[l]! * cN) / cT)));
      }
      return (layer, a, b) => b > (lo[layer] ?? 0) && a < (hi[layer] ?? 0);
    },
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// REAL mode — the actual provenance graph, layered + wired from real edges.
// ═════════════════════════════════════════════════════════════════════════════
function recordOf(node: { data?: Record<string, unknown> } | undefined): Record<string, unknown> {
  if (!node) return {};
  const data = (node.data ?? {}) as Record<string, unknown>;
  const vj = data.value_json;
  if (vj && typeof vj === "object" && !Array.isArray(vj)) return { ...(vj as Record<string, unknown>), ...data };
  return data;
}

export function buildRealModel(graph: Graph, index: GraphIndex): ScaleModel {
  // longest-path depth (Kahn) — sources (indeg 0) at depth 0, matching buildMap.
  const indeg = new Map<string, number>();
  for (const node of graph.nodes) indeg.set(node.id, index.predecessors.get(node.id)?.length ?? 0);
  const depth = new Map<string, number>();
  const queue: string[] = [];
  for (const node of graph.nodes) if ((indeg.get(node.id) ?? 0) === 0) (depth.set(node.id, 0), queue.push(node.id));
  for (let qi = 0; qi < queue.length; qi++) {
    const u = queue[qi]!;
    const du = depth.get(u) ?? 0;
    for (const v of index.successors.get(u) ?? []) {
      if (du + 1 > (depth.get(v) ?? -1)) depth.set(v, du + 1);
      const d = (indeg.get(v) ?? 0) - 1;
      indeg.set(v, d);
      if (d === 0) queue.push(v);
    }
  }
  let maxDepth = 0;
  for (const d of depth.values()) maxDepth = Math.max(maxDepth, d);
  // Any node still with indeg > 0 sits in a cycle (provenance graphs should be DAGs, but be robust) —
  // park it in a dedicated layer beyond the acyclic max instead of letting it pollute layer 0 (sources).
  let cyclic = false;
  for (const node of graph.nodes) if (!depth.has(node.id)) (depth.set(node.id, maxDepth + 1), (cyclic = true));
  if (cyclic) maxDepth += 1;
  const nL = Math.max(1, maxDepth + 1);

  // members per layer (insertion order), + reverse lookup id → {layer, idx}.
  const layerNodes: string[][] = Array.from({ length: nL }, () => []);
  const where = new Map<string, { L: number; i: number }>();
  for (const node of graph.nodes) {
    const L = depth.get(node.id) ?? 0;
    const arr = layerNodes[L]!;
    where.set(node.id, { L, i: arr.length });
    arr.push(node.id);
  }
  const count = layerNodes.map((a) => a.length);
  const maxC = Math.max(1, ...count);
  const bandH = bandHeights(count, maxC);

  // adjacent-layer edge wiring: per (L) a list of [srcIdx, dstIdx] for edges L→L+1.
  const wiring: Array<Array<[number, number]>> = Array.from({ length: Math.max(0, nL - 1) }, () => []);
  for (const e of graph.edges) {
    const s = where.get(e.source),
      t = where.get(e.target);
    if (s && t && t.L === s.L + 1) wiring[s.L]!.push([s.i, t.i]);
  }
  const flow = wiring.map((w) => w.length);
  let maxFlow = 0;
  for (const f of flow) if (f > maxFlow) maxFlow = f;

  const dominantKind = (L: number) => {
    const tally = new Map<string, number>();
    for (const id of layerNodes[L]!) {
      const k = index.nodeById.get(id)?.type ?? "node";
      tally.set(k, (tally.get(k) ?? 0) + 1);
    }
    let best = "node",
      n = -1;
    for (const [k, c] of tally) if (c > n) ((best = k), (n = c));
    return best;
  };
  const kindCache = new Array<string>(nL);
  const layerKind = (L: number) => (kindCache[L] ??= dominantKind(L));

  return {
    mode: "real",
    n: graph.nodes.length,
    nL,
    count,
    bandH,
    flow,
    maxFlow,
    maxC,
    fitZ: 0,
    layerKind,
    realIdAt: (L, i) => layerNodes[L]?.[i] ?? null,
    tileInfo(L, i0, i1) {
      const size = i1 - i0;
      if (size === 1) {
        const node = index.nodeById.get(layerNodes[L]![i0]!);
        return { single: true, title: node?.label ?? layerNodes[L]![i0]!, sub: node?.type ?? null };
      }
      return { single: false, title: `${fmtCount(size)} ${shortKind(layerKind(L))}s`, sub: `layer ${L + 1}` };
    },
    detail(L, i0, i1) {
      const size = i1 - i0,
        kind = layerKind(L),
        short = shortKind(kind);
      if (size === 1) {
        const id = layerNodes[L]![i0]!,
          node = index.nodeById.get(id);
        const up = Math.max(0, ancestors(index, id, 64).ids.size - 1);
        const down = Math.max(0, descendants(index, id, 64).ids.size - 1);
        const inputs = index.predecessors.get(id)?.length ?? 0;
        return { kind: node?.type ?? kind, title: node?.label ?? id, up, down, inputs, json: JSON.stringify(recordOf(node), null, 2), realId: id };
      }
      const obj = { group: `${fmtCount(size)} ${short}s`, kind, layer: `${L + 1} of ${nL}`, members: layerNodes[L]!.slice(i0, i1).slice(0, 8) };
      return { kind, title: `${fmtCount(size)} ${short}s`, up: 0, down: 0, inputs: 0, json: JSON.stringify(obj, null, 2), realId: null };
    },
    connects(L, i0, i1, j0, j1) {
      const w = wiring[L];
      if (!w) return false;
      for (const [si, ti] of w) if (si >= i0 && si < i1 && ti >= j0 && ti < j1) return true;
      return false;
    },
    coneOf(L, i0, i1) {
      // BFS the REAL lineage of the selected members → on-lineage node-index sets per layer.
      const sets: Array<Set<number>> = Array.from({ length: nL }, () => new Set<number>());
      for (let i = i0; i < i1; i++) {
        const seed = layerNodes[L]?.[i];
        if (!seed) continue;
        const reach = new Set<string>([seed]);
        for (const id of ancestors(index, seed, 64).ids) reach.add(id);
        for (const id of descendants(index, seed, 64).ids) reach.add(id);
        for (const id of reach) {
          const w = where.get(id);
          if (w) sets[w.L]!.add(w.i);
        }
      }
      return (layer, a, b) => {
        const set = sets[layer];
        if (!set) return false;
        for (let i = a; i < b; i++) if (set.has(i)) return true;
        return false;
      };
    },
  };
}
